import IItem from '../product_delivery';
import {Details, documentTypes, Payment} from './utils';

export type DocumentType =
    'receipt'
    | 'invoice'
    | 'tax_invoice'
    | 'order'
    | 'delivery_certificate'
    | 'refund'
    | 'supplier_note'
    | 'debt'
    | 'visit_note'
    | 'delivery_returns'
    | 'sup_invoice'
    | 'sup_receipt'
    | 'sup_tax_invoice'
    | 'sup_refund'
|'sup_debt'
    | 'empty'
    ;

export function docTypeToDocType(types) {
    return types.map(
        (type) => {
            switch (type) {
                case 'receipt':
                    return documentTypes.receipt.nkType;
                case 'invoice':
                    return documentTypes.invoice.nkType;
                case 'tax_invoice':
                    return documentTypes.tax_invoice.nkType;
                case 'order':
                    return documentTypes.order.nkType;
                case 'delivery_certificate':
                    return documentTypes.delivery_certificate.nkType;
                case 'refund':
                    return documentTypes.refund.nkType;
                case 'supplier_note':
                    return documentTypes.supplier_note.nkType;
                case 'debt':
                    return documentTypes.debt.nkType;
                case 'visit_note':
                    return documentTypes.visit_note.nkType;
                case 'delivery_returns':
                    return documentTypes.delivery_certificate.nkType;
                case 'sup_invoice'    :
                    return documentTypes.sup_invoice.nkType;
                case 'sup_receipt'    :
                    return documentTypes.sup_receipt.nkType;
                case 'sup_tax_invoice':
                    return documentTypes.sup_tax_invoice.nkType;
                case 'sup_refund'      :
                    return documentTypes.sup_refund.nkType;
                case 'sup_debt'      :
                    return documentTypes.sup_debt.nkType;
                case 'empty':
                    return 0;

            }
        }
    )

}
export function isSupplierDocument(type: DocumentType) {
    return type === 'supplier_note' || type === 'sup_invoice' || type === 'sup_receipt' || type === 'sup_tax_invoice' || type === 'sup_refund' || type === 'sup_debt';
}

/** Printed supplier invoice number (אסמכתא) — not the ERP document id. */
export function supplierInvoiceNumberOf(doc: any): string {
    if (!doc) return '';
    const fromRow = doc.supplierInvoiceNumber;
    if (fromRow != null && String(fromRow).trim() !== '') return String(fromRow).trim();
    const nk = doc.nkObject;
    if (typeof nk?.supplierInvoiceNumber === 'function') {
        const v = nk.supplierInvoiceNumber();
        return v == null ? '' : String(v).trim();
    }
    const header = doc.header;
    const fromHeader = header?.supplier_invoice_number ?? header?.supplierInvoiceNumber;
    return fromHeader == null || fromHeader === '' ? '' : String(fromHeader).trim();
}

function isSupplierTaxNoteType(type: any): boolean {
    return type === 'sup_invoice' || type === 'sup_tax_invoice' || type === 'sup_refund';
}

/** Matches server `DocumentPdfFlags.PRINTED` on delivery + tax `pdf`. */
export const DOC_PDF_PRINTED = 1;

export const PRINTABLE_NOTE_TYPES = new Set<string>([
    'delivery_certificate', 'invoice', 'tax_invoice', 'refund',
    'supplier_note', 'sup_invoice', 'sup_tax_invoice', 'sup_refund',
]);

/** True when the printed bit (or boolean true) is set on a delivery / tax note. */
export function isDocumentPrinted(s?: { pdf?: boolean | number; header?: any; nkObject?: any } | null): boolean {
    if (!s) return false;
    const raw = s.pdf ?? s.header?.pdf ?? s.nkObject?.pdf;
    if (raw === true) return true;
    const n = Number(raw);
    return Number.isFinite(n) && (n & DOC_PDF_PRINTED) !== 0;
}

/** True when every printable note on the row (DV / invoice / tax note) has been printed. */
export function notesArePrinted(docs?: { pdf?: boolean | number; type?: string }[] | null): boolean {
    const printable = (docs ?? []).filter((s) => !s?.type || PRINTABLE_NOTE_TYPES.has(s.type));
    return printable.length > 0 && printable.every(isDocumentPrinted);
}

/**
 * Visible document number for tables / carteset / pickers.
 * Supplier tax notes show `supplierInvoiceNumber` when set; otherwise the internal id.
 */
export function displayDocumentId(doc: any): string {
    if (!doc) return '';
    const src = doc.doc && (doc.doc.id != null || doc.doc.type) ? doc.doc : doc;
    const nk = src.nkObject ?? doc.nkObject;
    if (typeof src.displayDocId === 'function') {
        const v = src.displayDocId();
        if (v != null && String(v).trim() !== '') return String(v);
    }
    if (typeof nk?.displayDocId === 'function') {
        const v = nk.displayDocId();
        if (v != null && String(v).trim() !== '') return String(v);
    }
    const type = src.type ?? doc.type;
    if (isSupplierTaxNoteType(type)) {
        const inv = supplierInvoiceNumberOf(src);
        if (inv) return inv;
    }
    const id = src.id ?? doc.id;
    if (id == null || id === '' || id === 0 || id === '0') return '';
    return String(id);
}

function kotlinListToJsArray(list: any): any[] {
    if (list == null) return [];
    if (Array.isArray(list)) return list;
    if (typeof list.toArray === 'function') {
        try {
            const a = list.toArray();
            if (Array.isArray(a)) return a;
        } catch {
            /* ignore */
        }
    }
    const size = Number(list.size);
    if (!Number.isFinite(size) || size <= 0) return [];
    const out: any[] = [];
    for (let i = 0; i < size; i++) {
        out.push(typeof list.get === 'function' ? list.get(i) : list[i]);
    }
    return out;
}

/**
 * Visible linked-invoice numbers on a receipt. Internal `receiptsIds` stay ERP ids;
 * supplier receipts show the printed supplier invoice number when known.
 */
export function displayLinkedInvoiceIds(doc: any): string[] {
    if (!doc) return [];
    if (Array.isArray(doc.receiptsDisplayIds) && doc.receiptsDisplayIds.length) {
        return doc.receiptsDisplayIds.map(String).filter((s) => s && s !== '0');
    }
    const fromHeader = doc.header?.taxNotesDisplay;
    if (Array.isArray(fromHeader) && fromHeader.length) {
        return fromHeader.map(String).filter(Boolean);
    }
    const nk = doc.nkObject;
    if (typeof nk?.displayTaxNoteIds === 'function') {
        try {
            const arr = kotlinListToJsArray(nk.displayTaxNoteIds());
            if (arr.length) return arr.map(String).filter(Boolean);
        } catch {
            /* cache miss / older bundle */
        }
    }
    return (doc.receiptsIds ?? []).map(String).filter((s) => s && s !== '0');
}

export type DocumentStatus = 'open' | 'approved' | 'pending_approval' | 'closed' | 'canceled' | 'partly_closed';
export function docStatusToNum(type: DocumentStatus) {
    switch (type) {
        case 'open':
            return 1;
        case 'partly_closed':
            return 1;
        case 'approved':
            return 1;
        case 'pending_approval':
            return 1;
        case 'closed':
            return 2;
        case 'canceled':
            return 0;
    }

}

export const documentStatuses = {
    partly_closed: 'נסגר חלקית',
    open: 'פתוח',
    approved: 'אושר',
    pending_approval: 'ממתין לאישור',
    closed: 'סגור',
    canceled: 'בוטל',


};

export interface Document {
    id: number;
    entId: string;
    entName: string;
    entBName: string;
    // date: string; // yyyy-mm-dd
    date: Date;
    end: Date;
    issueDate: Date;
    type: DocumentType;


    status: DocumentStatus;
    agent: string;
    mainValue: number;
    taxValue?: number;
    preValue?: number;
    ref_id?: string
    nkObject: any;


}

export interface ExtendedDocument extends Document {

    comment: string;
    category?: string;
    sign?: boolean;
    photo?: boolean;
    photoCount?: number;
    pdf?: boolean
    // metadata
    color: string;
    textColor: string;
    baseColor: string;
    description: string;
    title: string;
    name: string;
    allDay: boolean;
    notvalid?: boolean;
    storeNext?: any;
    hashavshevet?: any;
    manager?: any;
    one?: any;
    sentToExternalSoftware?: boolean;
    externalSoftware?: any;
    driver?: string
    collector?:string
    orderId?: number
    agent_id?: number
    notes2?: string
    notes3?: string
    notes4?: string
    notes5?: string
    notes6?: string
    notes7?: string
    notes8?: string
    notes9?: string
    notes10?: string
    taxConfirmation?: string
    supplierInvoiceNumber?: string
    supplierInputEquipment?: boolean
    // Stage 3 JSON boundary: parsed JSON header from JSDB().documentToJson
    header?: any

}

export interface ProductNote extends ExtendedDocument {
    bul?: number;
    orderStatus?: string;
    user_id?: number;
    products: IItem[];
    returnsOnly?: boolean

}

export interface PaymentNote extends ExtendedDocument {
    details?: Details;
    payments: Payment[];
    refundRest?: boolean
}


export const typeStr = (doc): string => documentTypes[doc.type].name;
